"use client"

/**
 * PostDownloadPanel
 *
 * Shown after a successful download (both on sound-detail pages and in Chime
 * Studio).
 *
 * SHAPE: the panel shows the install path and nothing else. Everything that
 * isn't install sits behind one "More options" disclosure, collapsed.
 *
 *   ALWAYS VISIBLE — the install path
 *     1. GUIDE BRIDGE   — install-guide link
 *     2. EMAIL FILE     — "Email me this file"; on a phone this *is* the
 *                         install path (you can't reach a USB stick from iOS),
 *                         so it is not an optional extra
 *   BEHIND "MORE OPTIONS"
 *     3. NEWSLETTER     — Chime of the Week inline subscribe
 *     4. SHARE-BACK     — pre-filled X / Reddit posts
 *     5. SHARE ROW      — share sheet + share card (sound-detail only)
 *     6. SELF-REPORT    — one-time "Did it work?" yes/no (dismissible)
 *
 * WHY: 14 days to 2026-07-30, 4,488 people completed a download and 94 (2.1%)
 * clicked this panel's primary CTA. The panel was stacking five sections under
 * that CTA, so it read as a page footer at exactly the moment the user's
 * attention was on the browser download shelf. Collapsing the non-install
 * sections is the tested hypothesis: the panel wasn't failing to persuade, it
 * was failing to focus. Target metric is guide_visited_post_download ÷
 * download_completed; guardrails are newsletter signups (source
 * 'post_download') and email_file_requested, neither of which may fall.
 *
 * The panel is a single composed component so both placements stay in sync.
 * Placement owners (sound-player-client.tsx, download-panel.tsx) pass only
 * the sound metadata — no internal state is shared upward.
 */

import { useState, useCallback, useEffect, useRef } from "react"
import Link from "next/link"
import {
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Share2,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { withRequestProofHeaders } from "@/lib/request-proof"
import { buildSoundShareBack } from "@/lib/social-share-copy"
import {
  trackGuideVisitedPostDownload,
  trackInstallSelfReportedComplete,
  trackEmailFileRequested,
  trackDownloadConfirmShown,
  trackShareBackClick,
} from "@/lib/tracking"
import { useSafeLocalStorage } from "@/lib/hydration-utils"
import { play } from "cuelume"

// ── Constants ────────────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, "") || "https://teslalocksound.com"

/** Canonical sound URL + share-back text/links — shared builder in lib/social-share-copy. */
function buildShareBack(soundId: string, soundTitle: string) {
  return buildSoundShareBack(SITE_URL, soundId, soundTitle, soundId)
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface PostDownloadPanelProps {
  soundId: string
  soundTitle: string
  /** Optional: elapsed seconds since download completed (for funnel event) */
  secondsSinceDownload?: number
  /** Where the panel is mounted — drives the download_confirm_shown surface dimension */
  surface?: "sound_detail" | "chime_studio"
  className?: string
  /** Restore existing share affordances when rendered in the sound-detail player */
  onShare?: () => void
  onCreateShareCard?: () => void
}

// ── Self-report persistence ──────────────────────────────────────────────────

const SELF_REPORT_DISMISSED_KEY = "tls.selfreport.dismissed"

// ── Main component ───────────────────────────────────────────────────────────

export function PostDownloadPanel({
  soundId,
  soundTitle,
  secondsSinceDownload = 0,
  surface = "sound_detail",
  className,
  onShare,
  onCreateShareCard,
}: PostDownloadPanelProps) {
  // Fire the confirmation-shown funnel event once when the panel mounts
  // (the panel only renders after a successful download).
  const confirmTracked = useRef(false)
  useEffect(() => {
    if (confirmTracked.current) return
    confirmTracked.current = true
    trackDownloadConfirmShown({ sound_id: soundId, sound_title: soundTitle, surface })
  }, [soundId, soundTitle, surface])

  const shareBack = buildShareBack(soundId, soundTitle)

  const handleShareBack = useCallback(
    (channel: "x" | "reddit") => {
      trackShareBackClick({ sound_id: soundId, sound_title: soundTitle, channel })
    },
    [soundId, soundTitle]
  )

  // Non-install extras, collapsed by default so the guide bridge stands alone.
  const [extrasOpen, setExtrasOpen] = useState(false)

  // Self-report state
  const [selfReportDismissed, setSelfReportDismissed] = useSafeLocalStorage<boolean>(
    SELF_REPORT_DISMISSED_KEY,
    false
  )
  const [selfReportAnswer, setSelfReportAnswer] = useState<"success" | "failed" | null>(null)

  // Newsletter section
  const [newsletterEmail, setNewsletterEmail] = useState("")
  const [newsletterState, setNewsletterState] = useState<"idle" | "loading" | "done" | "error">(
    "idle"
  )
  const [newsletterMessage, setNewsletterMessage] = useState("")

  // Email-me-file section — collapsed by default to keep the panel tight
  const [emailFileOpen, setEmailFileOpen] = useState(false)
  const [emailFileAddr, setEmailFileAddr] = useState("")
  const [emailFileJoinNewsletter, setEmailFileJoinNewsletter] = useState(false)
  const [emailFileState, setEmailFileState] = useState<"idle" | "loading" | "done" | "error">(
    "idle"
  )
  const [emailFileMessage, setEmailFileMessage] = useState("")

  // ── Self-report handlers ──────────────────────────────────────────────────

  const handleSelfReport = useCallback(
    (outcome: "success" | "failed") => {
      setSelfReportAnswer(outcome)
      setSelfReportDismissed(true)
      if (outcome === "success") play("success")
      trackInstallSelfReportedComplete({
        outcome,
        came_from_download: true,
      })
    },
    [setSelfReportDismissed]
  )

  const handleSelfReportDismiss = useCallback(() => {
    setSelfReportDismissed(true)
  }, [setSelfReportDismissed])

  // ── Newsletter handler ────────────────────────────────────────────────────

  const handleNewsletterSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!newsletterEmail.trim() || !EMAIL_REGEX.test(newsletterEmail)) return

      setNewsletterState("loading")
      play("loading")
      try {
        const headers = await withRequestProofHeaders({ "Content-Type": "application/json" })
        const res = await fetch("/api/newsletter", {
          method: "POST",
          headers,
          body: JSON.stringify({ email: newsletterEmail.trim(), source: "post_download" }),
        })
        const data = (await res.json()) as { message?: string; error?: string; alreadySubscribed?: boolean }
        if (res.ok || data.alreadySubscribed) {
          setNewsletterState("done")
          setNewsletterMessage(data.message ?? "You're on the list.")
          play("success")
        } else {
          setNewsletterState("error")
          setNewsletterMessage(data.error ?? "Couldn't subscribe right now. Try again.")
          play("error")
        }
      } catch {
        setNewsletterState("error")
        setNewsletterMessage("Couldn't subscribe right now. Try again.")
        play("error")
      }
    },
    [newsletterEmail]
  )

  // ── Email-file handler ────────────────────────────────────────────────────

  const handleEmailFile = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!emailFileAddr.trim() || !EMAIL_REGEX.test(emailFileAddr)) return

      setEmailFileState("loading")
      play("loading")
      try {
        const headers = await withRequestProofHeaders({ "Content-Type": "application/json" })
        const res = await fetch("/api/email-sound", {
          method: "POST",
          headers,
          body: JSON.stringify({
            email: emailFileAddr.trim(),
            soundId,
            joinNewsletter: emailFileJoinNewsletter,
          }),
        })
        const data = (await res.json()) as { message?: string; error?: string }
        if (res.ok) {
          setEmailFileState("done")
          setEmailFileMessage(data.message ?? "Check your inbox.")
          // The file is prepared and on its way rather than saved to this
          // device, so it resolves on `ready` instead of the download's
          // `success` — the two outcomes should not sound identical.
          play("ready")
          trackEmailFileRequested({
            sound_id: soundId,
            join_newsletter: emailFileJoinNewsletter,
          })
        } else if (res.status === 429) {
          setEmailFileState("error")
          setEmailFileMessage("Too many requests. Try again in an hour.")
          play("error")
        } else {
          setEmailFileState("error")
          setEmailFileMessage(data.error ?? "Couldn't send right now. Try again.")
          play("error")
        }
      } catch {
        setEmailFileState("error")
        setEmailFileMessage("Couldn't send right now. Try again.")
        play("error")
      }
    },
    [emailFileAddr, emailFileJoinNewsletter, soundId]
  )

  // ── Guide bridge click tracking ───────────────────────────────────────────

  const handleGuideClick = useCallback(() => {
    trackGuideVisitedPostDownload({
      sound_id: soundId,
      sound_title: soundTitle,
      download_source: "sound_player",
      seconds_since_download: secondsSinceDownload,
    })
  }, [soundId, soundTitle, secondsSinceDownload])

  // ── Shared style tokens ───────────────────────────────────────────────────

  const inputClass =
    "w-full rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-canvas)] px-3 py-2 text-sm text-[var(--tls-fg-strong)] placeholder:text-[var(--tls-fg-subtle)] transition-[border-color] duration-[var(--motion-duration-standard)] focus:border-[color:var(--tls-brand)] focus:outline-none"

  // Form submits are the OUTLINED half of the filled/outlined pair: the
  // "next step" guide link above is this panel's one accent-filled primary
  // (§02.1 one primary per panel; owner ruling 2026-08-15).
  const primaryBtnClass =
    "flex min-h-[44px] items-center justify-center gap-2 rounded-[var(--ls-radius-0)] border border-[color:var(--tls-fg-strong)] bg-transparent px-4 text-sm font-semibold text-[var(--tls-fg-strong)] transition-[background-color,color,opacity] duration-[var(--motion-duration-standard)] hover:bg-[var(--tls-fg-strong)] hover:text-[var(--tls-canvas)] disabled:cursor-wait disabled:opacity-60"

  const ghostBtnClass =
    "flex min-h-[44px] items-center justify-center gap-2 rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-transparent px-4 text-sm font-medium text-[var(--tls-fg-muted)] transition-[border-color,background-color,color] duration-[var(--motion-duration-standard)] hover:border-[color:var(--tls-border-strong)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"

  return (
    <div
      className={cn(
        "space-y-4 border-t border-[color:var(--tls-border-subtle)] pt-4",
        className
      )}
    >
      {/* ── PRIMARY: confirmation + guide bridge ─────────────────────────
          The install path is the sacred, singular next step. Rule 1 gives
          content hairlines, not a card, even for the most important panel in
          the flow: a bottom rule reads as THE thing to do now without an
          enclosure. Everything below is explicitly optional and must not
          compete for this click. */}
      <div className="space-y-3 border-b border-[color:var(--tls-border-subtle)] pb-3">
        {/* Confirmation */}
        <section>
          <p className="flex items-center gap-2 text-sm font-semibold text-[var(--tls-fg-strong)]">
            <Check className="size-4 shrink-0" style={{ color: "var(--tls-brand-ink)" }} aria-hidden="true" />
            Your sound is downloaded
          </p>
          <p className="mt-0.5 text-xs" style={{ color: "var(--tls-fg-muted)" }}>
            Here&apos;s the 2-minute install guide to get it on your Tesla.
          </p>
        </section>

        {/* Guide bridge — the primary action */}
        <section>
          <p
            className="mb-1 tls-eyebrow"
            style={{ color: "var(--tls-fg-subtle)" }}
          >
            / next step
          </p>
          <Link
            href="/guide"
            onClick={handleGuideClick}
            className="flex min-h-[52px] w-full items-center justify-between gap-3 rounded-[var(--ls-radius-0)] bg-[var(--tls-brand)] px-4 py-3 text-[0.9375rem] font-semibold text-[var(--tls-brand-fg)] transition-[background-color] duration-[var(--motion-duration-standard)] hover:bg-[var(--tls-brand-hover)]"
          >
            <span className="flex items-center gap-2">
              How to install on your Tesla
            </span>
          </Link>
          <p className="mt-1.5 text-xs" style={{ color: "var(--tls-fg-subtle)" }}>
            On your phone?{" "}
            <Link
              href="/guide/mobile"
              className="text-[var(--tls-brand-ink)] underline-offset-2 hover:underline"
            >
              See the phone install guide
            </Link>
          </p>
        </section>

        {/* ── EMAIL FILE — install path, not an extra ──────────────────
            Stays inside the primary card: on a phone you cannot move a file
            to a USB stick, so emailing it to a desktop IS the install route.
            Burying it would also confound the mobile email-bridge experiment
            shipped 2026-07-15. */}
        <section className="border-t border-[color:var(--tls-border-subtle)] pt-3">
          <button
            type="button"
            onClick={() => setEmailFileOpen((v) => !v)}
            data-cuelume-press="press"
            data-cuelume-release="scan"
            className="flex min-h-[44px] w-full items-center justify-between text-sm"
            style={{ color: "var(--tls-fg-muted)" }}
            aria-expanded={emailFileOpen}
          >
            <span className="flex items-center gap-2">
              Email me this file
            </span>
            {emailFileOpen ? (
              <ChevronUp className="size-4 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
            )}
          </button>

          {emailFileOpen && (
            <div className="mt-3 space-y-2">
              {emailFileState === "done" ? (
                <p className="text-sm" style={{ color: "var(--tls-fg-muted)" }}>
                  <Check className="mr-1.5 inline size-3.5" style={{ color: "var(--tls-brand)" }} aria-hidden="true" />
                  {emailFileMessage}
                </p>
              ) : (
                <form onSubmit={handleEmailFile} className="space-y-2">
                  <p className="text-xs" style={{ color: "var(--tls-fg-subtle)" }}>
                    On mobile and can&apos;t move the file to a USB stick? We&apos;ll send a download
                    link to open on your computer.
                  </p>
                  <input
                    type="email"
                    value={emailFileAddr}
                    onChange={(e) => setEmailFileAddr(e.target.value)}
                    placeholder="your@email.com"
                    required
                    aria-label="Email address for file download link"
                    disabled={emailFileState === "loading"}
                    className={inputClass}
                  />
                  <label className="flex cursor-pointer items-center gap-2 text-xs" style={{ color: "var(--tls-fg-muted)" }}>
                    <input
                      type="checkbox"
                      checked={emailFileJoinNewsletter}
                      onChange={(e) => setEmailFileJoinNewsletter(e.target.checked)}
                      className="h-3.5 w-3.5 rounded accent-[var(--tls-brand)]"
                    />
                    Also subscribe to Chime of the Week (~6 sounds/month)
                  </label>
                  <button
                    type="submit"
                    disabled={emailFileState === "loading" || !emailFileAddr.trim()}
                    className={cn(primaryBtnClass, "w-full")}
                    data-cuelume-press="press"
                    data-cuelume-release="pulse"
                  >
                    {emailFileState === "loading" ? "Sending…" : "Send download link"}
                  </button>
                  {emailFileState === "error" && (
                    // Error verdict in ink at 700, no semantic red.
                    <p className="text-xs font-bold" style={{ color: "var(--tls-fg-strong)" }}>
                      {emailFileMessage}
                    </p>
                  )}
                </form>
              )}
            </div>
          )}
        </section>
      </div>

      {/* ── NON-INSTALL EXTRAS ───────────────────────────────────────────
          Newsletter, share and the install check-in. Collapsed by default:
          none of them is the next step, and each one visible is one more
          thing competing with the guide bridge above. */}
      <button
        type="button"
        onClick={() => setExtrasOpen((v) => !v)}
        data-cuelume-press="press"
        data-cuelume-release="scan"
        className="flex min-h-[44px] w-full items-center justify-between tls-eyebrow"
        style={{ color: "var(--tls-fg-subtle)" }}
        aria-expanded={extrasOpen}
      >
        <span>/ more options</span>
        {extrasOpen ? (
          <ChevronUp className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
        )}
      </button>

      {extrasOpen && (
      <>
      {/* Newsletter */}
      <section>
        {newsletterState === "done" ? (
          <p className="text-sm" style={{ color: "var(--tls-fg-muted)" }}>
            <Check className="mr-1.5 inline size-3.5" style={{ color: "var(--tls-brand)" }} aria-hidden="true" />
            {newsletterMessage}
          </p>
        ) : (
          <>
            <p
              className="mb-1 tls-eyebrow"
              style={{ color: "var(--tls-fg-subtle)" }}
            >
              / chime of the week
            </p>
            <p className="mb-2 text-sm" style={{ color: "var(--tls-fg-muted)" }}>
              ~6 new sounds per month, no spam.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="your@email.com"
                required
                aria-label="Email address for Chime of the Week"
                disabled={newsletterState === "loading"}
                className={cn(inputClass, "flex-1")}
              />
              <button
                type="submit"
                disabled={newsletterState === "loading" || !newsletterEmail.trim()}
                className={cn(primaryBtnClass, "shrink-0")}
                data-cuelume-press="press"
                data-cuelume-release="pulse"
              >
                {newsletterState === "loading" ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
            {newsletterState === "error" && (
              // Error verdict in ink at 700, no semantic red.
              <p className="mt-1.5 text-xs font-bold" style={{ color: "var(--tls-fg-strong)" }}>
                {newsletterMessage}
              </p>
            )}
          </>
        )}
      </section>

      {/* ── SHARE-BACK (pre-filled X / Reddit) ────────────────────────── */}
      <section className="border-t border-[color:var(--tls-border-subtle)] pt-3">
        <p
          className="mb-1 tls-eyebrow"
          style={{ color: "var(--tls-fg-subtle)" }}
        >
          / share back
        </p>
        <p className="mb-2 text-sm" style={{ color: "var(--tls-fg-muted)" }}>
          Tell other Tesla owners which sound you picked.
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href={shareBack.x}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleShareBack("x")}
            className={cn(ghostBtnClass, "flex-1")}
          >
            <Share2 className="size-4" aria-hidden="true" />
            Post on X
          </a>
          <a
            href={shareBack.reddit}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleShareBack("reddit")}
            className={cn(ghostBtnClass, "flex-1")}
          >
            <Share2 className="size-4" aria-hidden="true" />
            Post on Reddit
          </a>
        </div>
      </section>

      {/* ── SHARE ROW (sound-detail player only) ──────────────────────── */}
      {(onShare || onCreateShareCard) && (
        <section className="border-t border-[color:var(--tls-border-subtle)] pt-3 flex flex-wrap gap-2">
          {onShare && (
            <button
              type="button"
              onClick={onShare}
              className={cn(ghostBtnClass, "flex-1")}
            >
              <Share2 className="size-4" aria-hidden="true" />
              Share this sound
            </button>
          )}
          {onCreateShareCard && (
            <button
              type="button"
              onClick={onCreateShareCard}
              className={cn(ghostBtnClass, "flex-1")}
            >
              Create a share card
            </button>
          )}
        </section>
      )}

      {/* ── INSTALL CHECK-IN ─────────────────────────────────────────────
          Behind the disclosure: "did it work?" is incoherent seconds after a
          download, before the user has gone anywhere near their car. The same
          check-in lives at the bottom of /guide, which is where the question
          actually lands, so collapsing it here costs no real signal. */}
      {!selfReportDismissed && selfReportAnswer === null && (
        <section className="border-t border-[color:var(--tls-border-subtle)] pt-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm text-[var(--tls-fg-muted)]">
              Already installed it? Did it work on your car?
            </p>
            <button
              type="button"
              onClick={handleSelfReportDismiss}
              aria-label="Dismiss"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-[var(--tls-fg-subtle)] transition-colors hover:text-[var(--tls-fg-muted)]"
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              onClick={() => handleSelfReport("success")}
              className={cn(ghostBtnClass, "flex-1 border-[color:color-mix(in_srgb,var(--tls-brand-ink)_30%,transparent)] text-[var(--tls-brand-ink)] hover:bg-[color:color-mix(in_srgb,var(--tls-brand-ink)_8%,transparent)] hover:text-[var(--tls-brand-ink)]")}
            >
              <Check className="size-4" aria-hidden="true" />
              Yes, it worked
            </button>
            <button
              type="button"
              onClick={() => handleSelfReport("failed")}
              className={cn(ghostBtnClass, "flex-1")}
            >
              Not yet
            </button>
          </div>
        </section>
      )}

      {/* Install check-in: success acknowledgment */}
      {selfReportAnswer === "success" && (
        <p className="text-sm" style={{ color: "var(--tls-brand-ink)" }}>
          <Check className="mr-1.5 inline size-3.5" aria-hidden="true" />
          Nice, enjoy it.
        </p>
      )}

      {/* Install check-in: no answer — point to FAQ */}
      {selfReportAnswer === "failed" && (
        <p className="text-sm" style={{ color: "var(--tls-fg-muted)" }}>
          Check the{" "}
          <Link
            href="/faq#faq-compatibility"
            className="text-[var(--tls-brand-ink)] underline-offset-2 hover:underline"
          >
            compatibility section in the FAQ
          </Link>
          . Some older Model S/X don&apos;t support custom lock sounds.
        </p>
      )}
      </>
      )}
    </div>
  )
}
